import { useGrowl } from "PFApp/use_growl";
import { CustomValueBulkResponse } from "PFCore/hooks/queries/custom_fields/admin/custom_values_bulk/response";
import { PureCustomValue } from "PFTypes";
import { useTranslation } from "react-i18next";

import { SkillCounter, useSkillsManagementContext } from "../contexts/skills_management_context_provider";

type UseBulkResponseHandlerReturn = (
  response: CustomValueBulkResponse,
  options?: { targetSkill: PureCustomValue }
) => void | null;

export const useBulkResponseHandler = (): UseBulkResponseHandlerReturn => {
  const growl = useGrowl();
  const { t: translateFieldName } = useTranslation("core", { keyPrefix: "errors.customValues.fields" });
  const { t } = useTranslation("skillsManagement", { keyPrefix: "growl" });
  const { selectedCounter, setRowSelectionState } = useSkillsManagementContext();

  return (response: CustomValueBulkResponse, options) => {
    const failedRequest = response.find((entry) => {
      // don't display error growl when bulk approving approved and awaiting skills
      const isAlreadyApproved = entry.status === 422 && entry.event === "approve";
      return !!entry.data.errors && !isAlreadyApproved;
    });

    if (failedRequest) {
      const errorField = Object.keys(failedRequest.data.errors!)[0];
      const errorDetail = failedRequest.data.errors![errorField];
      growl({
        // @ts-ignore
        message: `${translateFieldName(errorField, "")} ${errorDetail}`,
        kind: "error"
      });
    }
    const successfulRequests = response.filter((entry) => !entry.data.errors);

    if (successfulRequests.length === 0) {
      return null;
    }

    // always after merge & block, after approve only if in approvals tab
    const idsToUnselect = successfulRequests
      .filter(
        ({ event }) =>
          ["block", "merge"].includes(event) ||
          (event === "approve" && selectedCounter === SkillCounter.Approvals)
      )
      .map(({ id }) => String(id));

    setRowSelectionState((previousSelection) => {
      const newSelection = {};
      Object.keys(previousSelection).forEach((id) => {
        if (!idsToUnselect.includes(id)) {
          newSelection[id] = true;
        }
      });
      return newSelection;
    });

    const { event, data } = successfulRequests[0];
    const { value: firstSkillName } = data;

    return growl({
      message: t(`success.${event}`, {
        count: successfulRequests.length,
        skillName: firstSkillName,
        targetSkill: event === "merge" && options?.targetSkill.text
      })
    });
  };
};
